import TopicSidebar from '../../components/topicsBar';
import PermissionErrorPage from '../shared/permissionError';
import { useUser } from '../../contexts/UserContext';
import { useEffect, useMemo, useState } from 'react';
import { knowledgeTracingService } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import '../../styles/components/pages/student-dashboard.css';
import '../../styles/components/pages/kc-mastery.css';

function getMasteryStatus(score) {
  if (score >= 80) {
    return { label: 'Mastered', className: 'mastered' };
  }

  if (score >= 50) {
    return { label: 'In Progress', className: 'in-progress' };
  }

  return { label: 'Needs Improvement', className: 'needs-improvement' };
}

function formatAxisValue(value) {
  if (value >= 10) {
    return Math.round(value).toString();
  }

  if (value >= 1) {
    return value.toFixed(1).replace(/\.0$/, '');
  }

  return value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '') || '0';
}

const EXCLUDE_ZERO_PERCENT_KCS = true;
const USE_DUMMY_KC_DATA = false;

const DUMMY_KC_VECTORS = [
  {
    kc_vector: {
      'KC 1': 18,
      'KC 2': 24,
      'KC 3': 31,
      'KC 4': 28,
      'KC 5': 42,
      'KC 6': 36,
      'KC 7': 22,
      'KC 8': 47,
      'KC 9': 39,
      'KC 10': 51,
      'KC 11': 34,
      'KC 12': 44,
      'KC 13': 78,
      'KC 14': 82,
    },
  },
  {
    kc_vector: {
      'KC 1': 22,
      'KC 2': 29,
      'KC 3': 35,
      'KC 4': 33,
      'KC 5': 48,
      'KC 6': 41,
      'KC 7': 27,
      'KC 8': 54,
      'KC 9': 43,
      'KC 10': 58,
      'KC 11': 39,
      'KC 12': 50,
      'KC 13': 84,
      'KC 14': 88,
    },
  },
  {
    kc_vector: {
      'KC 1': 27,
      'KC 2': 34,
      'KC 3': 40,
      'KC 4': 38,
      'KC 5': 55,
      'KC 6': 47,
      'KC 7': 32,
      'KC 8': 61,
      'KC 9': 49,
      'KC 10': 64,
      'KC 11': 45,
      'KC 12': 57,
      'KC 13': 91,
      'KC 14': 94,
    },
  },
];

export default function KnowledgeComponentsPage() {
  const { roleData, user } = useUser();
  const [kcVectors, setKcVectors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const canViewStudent = roleData === 'student' || roleData === 'instructor';

  useEffect(() => {
    if (!canViewStudent) {
      setIsLoading(false);
      return;
    }

    if (USE_DUMMY_KC_DATA) {
      setError('');
      setIsLoading(false);
      setKcVectors(DUMMY_KC_VECTORS);
      return;
    }

    if (!user?.studentID) {
      setKcVectors([]);
      setError('');
      setIsLoading(false);
      return;
    }

    let isActive = true;

    const fetchKcData = async () => {
      setIsLoading(true);
      setError('');

      try {
        const data = await knowledgeTracingService.getAllKcsForUser(user.studentID);
        if (!isActive) return;
        setKcVectors(Array.isArray(data) ? data : []);
      } catch (fetchError) {
        if (!isActive) return;
        console.error('Error fetching KC mastery data:', fetchError);
        setError(fetchError?.message || 'Failed to load KC mastery data.');
        setKcVectors([]);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    fetchKcData();

    return () => {
      isActive = false;
    };
  }, [canViewStudent, user?.studentID]);

  const { masteryRows, masteryTrend, barValues, scaledBarValues, summaryCards, yAxisMax, yAxisTicks, averageValue, scaledAverageValue } = useMemo(() => {
    const kcHistory = new Map();

    kcVectors.forEach((entry, submissionIndex) => {
      const vector = entry?.kc_vector || {};

      Object.entries(vector).forEach(([kcName, rawValue]) => {
        const value = Number(rawValue);
        if (!Number.isFinite(value)) return;
        if (EXCLUDE_ZERO_PERCENT_KCS && value === 0) return;

        if (!kcHistory.has(kcName)) {
          kcHistory.set(kcName, []);
        }

        kcHistory.get(kcName).push({ submissionIndex, value });
      });
    });

    const masteryRows = [...kcHistory.entries()]
      .sort(([kcA], [kcB]) => kcA.localeCompare(kcB))
      .map(([kcName, history]) => {
        const values = history.map((point) => point.value);
        const latestValue = values[values.length - 1] ?? 0;
        const averageValue = values.length
          ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length)
          : 0;
        const status = getMasteryStatus(averageValue);

        return {
          component: kcName,
          status: status.label,
          statusClass: status.className,
          accuracy: averageValue,
          latestAccuracy: latestValue,
          attempts: values.length,
        };
      });

    const masteryTrend = kcVectors.map((entry) => {
      const values = Object.values(entry?.kc_vector || {})
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && (!EXCLUDE_ZERO_PERCENT_KCS || value > 0));

      if (!values.length) return 0;
      return Math.round(values.reduce((sum, value) => sum + value, 0) / values.length);
    });

    const barValues = masteryRows.map((row) => row.latestAccuracy);

    const latestAverage = masteryTrend.length ? masteryTrend[masteryTrend.length - 1] : 0;
    const statusCounts = masteryRows.reduce((counts, row) => {
      if (row.status === 'Mastered') counts.mastered += 1;
      else if (row.status === 'In Progress') counts.inProgress += 1;
      else counts.needsImprovement += 1;
      return counts;
    }, { mastered: 0, inProgress: 0, needsImprovement: 0 });

    const summaryCards = [
      { label: 'Total Components', value: masteryRows.length },
      { label: 'Mastered', value: statusCounts.mastered },
      { label: 'In Progress', value: statusCounts.inProgress },
      { label: 'Needs Improvement', value: statusCounts.needsImprovement },
    ];

    const yAxisSourceMax = Math.max(
      0,
      ...barValues,
      ...masteryTrend,
    );

    const yAxisMax = yAxisSourceMax >= 100
      ? 100
      : yAxisSourceMax > 0
        ? Math.min(100, Math.ceil(yAxisSourceMax * 1.15) + 1)
        : 100;

    const scaledBarValues = barValues.map((value) => {
      if (yAxisMax <= 0) return 0;
      return (value / yAxisMax) * 100;
    });

    const averageValue = barValues.length > 0
      ? barValues.reduce((sum, value) => sum + value, 0) / barValues.length
      : 0;
    const scaledAverageValue = yAxisMax > 0 ? (averageValue / yAxisMax) * 100 : 0;

    const yAxisTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => ({
      ratio,
      position: ratio * 100,
      value: yAxisMax * ratio,
    }));

    return {
      masteryRows,
      masteryTrend,
      barValues,
      scaledBarValues,
      summaryCards,
      latestAverage,
      yAxisMax,
      yAxisTicks,
      averageValue,
      scaledAverageValue,
    };
  }, [kcVectors]);

  const hasKcData = masteryRows.length > 0;
  const chartMinWidth = Math.max((masteryRows.length || 1) * 22, 720);
  const recommendedRows = masteryRows
    .filter((row) => row.status !== 'Mastered')
    .sort((rowA, rowB) => rowB.accuracy - rowA.accuracy)
    .slice(0, 5);

  function getRecommendationText(row, index) {
    if (row.status === 'In Progress') {
      const texts = [
        `Push ${row.component} a bit higher with one focused set.`,
        `Practice ${row.component} again to lock it in.`,
        `${row.component} is close — try one more attempt.`,
      ];

      return texts[index % texts.length];
    }

    const texts = [
      `Review ${row.component} basics before your next try.`,
      `Revisit ${row.component} with a short practice set.`,
      `Start with ${row.component} fundamentals and retry.`,
      `Slow down on ${row.component} and rebuild the steps.`,
    ];

    return texts[index % texts.length];
  }

  const recentActivityItems = hasKcData
    ? [
        `Tracked ${kcVectors.length} KC snapshot${kcVectors.length === 1 ? '' : 's'}`,
        `Latest average mastery: ${masteryTrend[masteryTrend.length - 1] ?? 0}%`,
        `Strongest KC in view: ${[...masteryRows].sort((a, b) => b.accuracy - a.accuracy)[0]?.component || '—'}`,
      ]
    : ['No KC activity has been recorded yet.'];

  if (!canViewStudent) {
    return <PermissionErrorPage />;
  }

  if (isLoading) {
    return (
      <div className="student-dashboard">
        <div id="sidebar">
          <TopicSidebar />
        </div>

        <div className="student-dashboard-content">
          <div className="student-dashboard-container kc-mastery-page">
            <LoadingSpinner text="Loading KC mastery data..." />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="student-dashboard">
      <div id="sidebar">
        <TopicSidebar />
      </div>

      <div className="student-dashboard-content">
        <div className="student-dashboard-container kc-mastery-page">
          <h1 className="student-dashboard-heading">Knowledge Component Mastery</h1>
          <p className="student-dashboard-subheading">
            Live mastery data from your stored KC vectors.
          </p>

          <div className="student-stats-grid kc-summary-grid">
            {summaryCards.map((card) => (
              <div className="stat-card kc-summary-card" key={card.label}>
                <div className="stat-card-header">
                  <span className="stat-card-label">{card.label}</span>
                </div>
                <div className="stat-card-value">{card.value}</div>
              </div>
            ))}
          </div>

          <div className="kc-main-grid">
            <section className="kc-panel kc-overview-panel" aria-label="Mastery overview chart">
              <h2 className="kc-panel-title">Mastery Overview</h2>
              {!hasKcData ? (
                <div className="kc-empty-state">
                  {error ? error : 'No KC vectors are available yet for this student.'}
                </div>
              ) : null}
              <div className="kc-chart-shell">
                <div className="kc-chart-canvas" style={{ minWidth: `${chartMinWidth}px` }}>
                  <div className="kc-y-axis-title" aria-hidden="true">Mastery %</div>
                  <div className="kc-y-axis" aria-hidden="true">
                    {yAxisTicks.map((tick) => (
                      <span
                        key={`y-label-${tick.ratio}`}
                        className="kc-y-axis-label"
                        style={{ bottom: `${tick.position}%` }}
                      >
                        {formatAxisValue(tick.value)}
                      </span>
                    ))}
                  </div>

                  <div className="kc-chart-grid-lines" aria-hidden="true">
                    {yAxisTicks.map((tick) => (
                      <span
                        key={`y-line-${tick.ratio}`}
                        className="kc-grid-line"
                        style={{ bottom: `${tick.position}%` }}
                      />
                    ))}
                  </div>

                  <div
                    className="kc-bar-series"
                    aria-hidden="true"
                    style={{ gridTemplateColumns: `repeat(${Math.max(barValues.length, 1)}, minmax(0, 1fr))` }}
                  >
                    {hasKcData ? (
                      scaledBarValues.map((value, idx) => (
                        <div className="kc-bar-item" key={`bar-${idx}`}>
                          <div className="kc-bar" style={{ height: `${value}%` }} />
                          <span className="kc-bar-label">{masteryRows[idx]?.component || `KC ${idx + 1}`}</span>
                        </div>
                      ))
                    ) : (
                      <div className="kc-bar-item">
                        <div className="kc-bar" style={{ height: '0%' }} />
                        <span className="kc-bar-label">No KC data</span>
                      </div>
                    )}
                  </div>

                  {hasKcData ? (
                    <div
                      className="kc-average-line"
                      style={{ bottom: `${scaledAverageValue}%` }}
                      aria-hidden="true"
                    >
                      <span className="kc-average-line-label">Avg {formatAxisValue(averageValue)}%</span>
                    </div>
                  ) : null}

                  <div className="kc-x-axis-label" aria-hidden="true">Knowledge Components</div>
                </div>
              </div>
            </section>

            <aside className="kc-side-stack" aria-label="Learning support information">
              <section className="kc-panel kc-info-panel kc-recent-panel">
                <h2 className="kc-panel-title">Recent Activity</h2>
                <ul className="kc-checklist">
                  {hasKcData ? (
                    <>
                      {recentActivityItems.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </>
                  ) : (
                    <li>No KC activity has been recorded yet.</li>
                  )}
                </ul>
              </section>

              <section className="kc-panel kc-info-panel kc-recommendations-panel">
                <h2 className="kc-panel-title">Recommendations</h2>
                <ul className="kc-checklist">
                  {hasKcData ? (
                    recommendedRows.length > 0 ? (
                      recommendedRows.map((row, index) => (
                        <li key={row.component}>{getRecommendationText(row, index)}</li>
                      ))
                    ) : (
                      <li>All visible KC rows are already mastered — keep using harder problems to maintain momentum.</li>
                    )
                  ) : (
                    <li>Complete a submission to generate KC mastery analytics.</li>
                  )}
                </ul>
              </section>
            </aside>
          </div>

          <section className="kc-panel kc-status-panel">
            <h2 className="kc-panel-title">Knowledge Component Status</h2>
            <div className="table-responsive">
              <table className="table table-hover kc-status-table">
                <thead>
                  <tr>
                    <th>Component</th>
                    <th>Status</th>
                    <th>Accuracy</th>
                    <th>Attempts</th>
                  </tr>
                </thead>
                <tbody>
                  {hasKcData ? (
                    masteryRows.map((row) => (
                      <tr key={row.component}>
                        <td>{row.component}</td>
                        <td>
                          <span className={`kc-status-pill ${row.statusClass}`}>
                            {row.status}
                          </span>
                        </td>
                        <td>{row.accuracy}%</td>
                        <td>{row.attempts}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                        {error || 'No KC mastery records found for this user.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
